import { faShield } from '@fortawesome/pro-regular-svg-icons'
import { faBookUser } from '@fortawesome/pro-regular-svg-icons/faBookUser'
import { faBoxes } from '@fortawesome/pro-regular-svg-icons/faBoxes'
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons/faCalendarClock'
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog'
import { faMessageLines } from '@fortawesome/pro-regular-svg-icons/faMessageLines'
import { faRightLeft } from '@fortawesome/pro-solid-svg-icons'
import { usePathname } from 'next/navigation'
import { type ReactNode } from 'react'

import { useUserVerified } from '@flecto/auth'
import { useTranslation } from '@flecto/translations'

import { Icon } from '../../@shared/Icon/Icon'
import useMediaQuery from '../../lib/utils/useMediaQuery'

export type Menu = {
  label: string
  path: string
  icon?: ReactNode
  omit?: boolean
  indicator?: boolean
  child?: Menu[]
}

export function useMenus() {
  const pathname = usePathname()
  const { isMobile } = useMediaQuery()
  const { t } = useTranslation('saas', { keyPrefix: 'saas-nav' })
  const { isUserVerified } = useUserVerified()

  const shouldRenderNavbar = ['/onboarding'].some(
    (menu) => !pathname.startsWith(menu)
  )

  const shouldRenderNavbarSub = ['/profile', '/user'].some((menu) =>
    pathname.startsWith(menu)
  )

  const menusSettings: Menu[] = [
    {
      label: t('account'),
      path: '/profile/user',
    },
    {
      label: t('billing'),
      path: '/profile/billing',
    },
    {
      label: t('verifications'),
      path: '/profile/verifications',
      indicator: !isUserVerified,
    },
    {
      label: t('my_business'),
      path: '/profile/business',
    },
    {
      label: t('my_store'),
      path: '/profile/store',
    },
    {
      label: t('locations'),
      path: '/profile/locations',
    },
    {
      label: t('bookings'),
      path: '/profile/bookings',
    },
    {
      label: t('inventory'),
      path: '/profile/inventory',
    },
    {
      label: t('taxes'),
      path: '/profile/taxes',
    },
    {
      label: t('insurances'),
      path: '/user/insurances',
    },
  ]

  const menus: Menu[] = [
    {
      label: t('schedule'),
      path: '/schedule',
      icon: <Icon size='md' icon={faCalendarClock} />,
    },
    {
      label: t('bookings'),
      path: '/bookings',
      icon: <Icon size='md' icon={faRightLeft} />,
    },
    {
      label: t('instant_insurance'),
      path: '/insurances',
      icon: <Icon size='md' icon={faShield} />,
      omit: isMobile,
    },
    {
      label: t('inquiries'),
      path: '/inquiries',
      icon: <Icon size='md' icon={faMessageLines} />,
    },
    {
      label: t('inventory'),
      path: '/items',
      icon: <Icon size='md' icon={faBoxes} />,
    },
    {
      label: t('customers'),
      path: '/customers',
      icon: <Icon size='md' icon={faBookUser} />,
    },
    {
      label: t('settings'),
      path: '/profile',
      icon: <Icon size='md' icon={faCog} />,
      indicator: !isUserVerified,
      child: menusSettings,
    },
  ]

  return { menus, menusSettings, shouldRenderNavbar, shouldRenderNavbarSub }
}
